import { createSkyBreathFlow } from './187-sky-breath-flow.mjs';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { createSkyNostrilVapour } from './160-sky-dragon-nostrils.mjs';

/** Original fire surfaces follow authoritative threat positions, velocities and
 * emission origins. The fading wake is decorative: only domain projectiles hit.
 * Shared resources and a fixed16-ember charge pool keep work bounded per frame. */
export function createSkyDragonFire(T, scene, own, reduced) {
  const coreGeometry = own(new T.IcosahedronGeometry(1, 2));
  const tailGeometry = own(new T.ConeGeometry(1, 1, 12, 5, true));
  tailGeometry.rotateX(-Math.PI / 2).translate(0, 0, -0.5);
  const wisps = [];
  for (let index = 0; index < 7; index++) {
    const angle = (index * Math.PI * 2) / 7;
    const direction = new T.Vector3(Math.cos(angle) * 0.8, Math.sin(angle) * 0.8, -0.7).normalize();
    const part = tailGeometry.clone().scale(0.24, 0.24, 0.8 + (index % 3) * 0.13);
    part.applyQuaternion(new T.Quaternion().setFromUnitVectors(new T.Vector3(0, 0, -1), direction));
    part.translate(Math.cos(angle) * 0.72, Math.sin(angle) * 0.72, -0.12);
    wisps.push(part);
  }
  const wispGeometry = own(mergeGeometries(wisps));
  for (const part of wisps) part.dispose();
  const time = { value: 0 },
    moving = { value: 1 };
  function surface(core = false, plasma = false) {
    return own(
      new T.ShaderMaterial({
        uniforms: {
          time,
          moving,
          core: { value: core ? 1 : 0 },
          plasma: { value: plasma ? 1 : 0 },
        },
        transparent: true,
        depthWrite: false,
        blending: T.AdditiveBlending,
        side: T.DoubleSide,
        toneMapped: false,
        vertexShader: `
        uniform float time; uniform float moving; uniform float core;
        varying vec3 firePosition; varying vec2 fireUv;
        void main(){
          fireUv=uv; vec3 p=position;
          float wave=sin(p.z*21.0-time*29.0)*sin(p.z*13.0+time*17.0);
          p.xy*=1.0+wave*0.18*moving*(1.0-core);
          p*=1.0-core*(0.045+0.045*sin(p.x*17.0+p.y*13.0+p.z*11.0+time*21.0*moving));
          firePosition=p;
          gl_Position=projectionMatrix*modelViewMatrix*vec4(p,1.0);
        }`,
        fragmentShader: `
        uniform float time; uniform float moving; uniform float core; uniform float plasma;
        varying vec3 firePosition; varying vec2 fireUv;
        void main(){
          float age=clamp(-firePosition.z,0.0,1.0);
          float turbulence=sin(fireUv.x*37.0+age*19.0-time*31.0*moving)
            *sin(fireUv.x*19.0-age*31.0+time*21.0*moving);
          float patches=sin(firePosition.x*17.0+time*11.0*moving)
            *sin(firePosition.y*13.0-time*17.0*moving)
            *sin(firePosition.z*19.0+time*7.0*moving);
          float radial=length(firePosition.xy);
          float heat=clamp(mix(1.0-age,0.86-radial*0.48+patches*0.24,core)+turbulence*0.14,0.0,1.0);
          vec3 fire=mix(vec3(0.7,0.025,0.003),vec3(1.15,0.22,0.008),smoothstep(0.05,0.65,heat));
          fire=mix(fire,vec3(1.5,0.8,0.13),pow(heat,12.0));
          vec3 ion=mix(vec3(0.5,0.02,0.22),vec3(1.4,0.3,0.7),heat);
          float fade=pow(1.0-age,1.5)*(0.55+0.4*heat);
          gl_FragColor=vec4(mix(fire,ion,plasma),mix(fade,0.78,core));
        }`,
      }),
    );
  }
  const fireCore = surface(true),
    fireTail = surface(false),
    ionCore = surface(true, true),
    ionTail = surface(false, true);
  const axis = new T.Vector3(0, 0, 1),
    direction = new T.Vector3();
  const fiery = (item) => ['fireball', 'flame'].includes(item.attackKind);
  function create(item) {
    const group = new T.Group(),
      fire = fiery(item);
    group.name = fire ? 'Guardian living fire' : 'Hostile plasma dart';
    group.add(
      new T.Mesh(coreGeometry, fire ? fireCore : ionCore),
      new T.Mesh(tailGeometry, fire ? fireTail : ionTail),
    );
    if (fire) {
      const tongues = new T.Mesh(wispGeometry, fireTail);
      tongues.name = 'Decorative flame tongues';
      group.add(tongues);
    }
    group.userData.fire = fire;
    return group;
  }
  function update(group, item) {
    group.position.set(item.x, item.y, item.z);
    const radius = Number.isFinite(item.radius) ? Math.max(0.04, item.radius) : 0.12;
    const flame = item.attackKind === 'flame',
      fire = fiery(item);
    direction.set(item.vx ?? 0, item.vy ?? 0, item.vz ?? 24);
    if (direction.lengthSq() < 1e-8) direction.copy(axis);
    const speed = direction.length();
    group.quaternion.setFromUnitVectors(axis, direction.normalize());
    const distance = item.origin
      ? Math.hypot(item.x - item.origin.x, item.y - item.origin.y, item.z - item.origin.z)
      : Math.max(0, (item.age ?? 0) * speed);
    const length = Math.min(distance, fire ? (flame ? 3.4 : 2.2) : 0.9);
    group.children[0].scale.set(radius, radius, radius * (fire ? 1.35 : 2.4));
    group.children[1].scale.set(
      radius * (fire ? 1.45 : 0.7),
      radius * (fire ? 1.45 : 0.7),
      Math.max(0.001, length),
    );
    group.children[1].visible = length > 0.01;
    if (group.children[2]) {
      group.children[2].scale.set(radius, radius, Math.min(radius, distance / 1.2));
      group.children[2].rotation.z = reduced() ? 0 : (item.age ?? 0) * 3.5;
    }
    // No ring glyph or independent orbit: the incandescent core marks the real
    // projectile and the wake stops at its immutable emission point.
  }
  const mouth = new T.Group();
  mouth.name = 'Dragon mouth ignition';
  mouth.visible = false;
  const glow = new T.Mesh(coreGeometry, fireCore);
  mouth.add(glow);
  const emberMaterial = own(new T.MeshBasicMaterial({ color: '#ffad4e', toneMapped: false }));
  const embers = new T.InstancedMesh(coreGeometry, emberMaterial, 16);
  embers.frustumCulled = false;
  mouth.add(embers);
  const light = new T.PointLight('#ff892c', 0, 5, 2);
  light.name = 'Dragon ignition light';
  scene.add(light);
  scene.add(mouth);
  const pose = new T.Object3D();
  const breath = createSkyBreathFlow(T, scene, own, time, moving);
  const nostrils = createSkyNostrilVapour(T, scene, own);
  function draw(state, camera) {
    const quiet = reduced(),
      boss = state.boss;
    time.value = quiet ? 0 : state.time;
    moving.value = quiet ? 0 : 1;
    breath.draw(state, camera, quiet);
    nostrils.draw(state, camera, quiet);
    const socket = boss?.anatomy?.attackSocket ?? boss?.attackSocket;
    const dragon = boss?.artVariant === 'first-dragon' || fiery(boss ?? {});
    const charge = ['charge', 'breath'].includes(boss?.attackPhase)
      ? Math.max(0, Math.min(1, boss.charge ?? 0))
      : 0;
    const attackAge = (boss?.age ?? 0) - (boss?.lastAttackAge ?? -10);
    const flash = attackAge >= 0 && attackAge < 0.16 ? 1 - attackAge / 0.16 : 0;
    mouth.visible = Boolean(dragon && socket && !boss.defeated && (charge > 0 || flash > 0));
    if (!mouth.visible) {
      light.intensity = 0;
      return;
    }
    mouth.position.set(socket.x, socket.y, socket.z);
    light.position.copy(mouth.position);
    glow.scale.setScalar(0.04 + charge * 0.16 + flash * 0.2);
    embers.count = quiet ? 0 : 16;
    for (let index = 0; index < embers.count; index++) {
      const phase = (((state.time * 1.7 + index / 16) % 1) + 1) % 1;
      const radius = (0.15 + (1 - phase) * 0.55) * charge;
      const angle = index * 2.39996 + state.time * 2;
      pose.position.set(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius,
        0.1 + (1 - phase) * 0.35,
      );
      pose.scale.setScalar(0.012 + phase * 0.018);
      pose.updateMatrix();
      embers.setMatrixAt(index, pose.matrix);
    }
    embers.instanceMatrix.needsUpdate = true;
    light.intensity = quiet ? 0 : charge * 2 + flash * 4;
  }
  return {
    create,
    update,
    draw,
    mouth,
    light,
    breath,
    nostrils,
    warmup: () => [...breath.warmup(), nostrils.warmup()],
    dispose() {
      breath.dispose();
      nostrils.dispose();
      mouth.removeFromParent();
      light.removeFromParent();
      embers.dispose();
    },
  };
}
